import { useEffect, useState } from "react";

import type { MigrationState, SourceBankAccount, SourceUnreconciledDerived } from "../../api/types";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { Field } from "../../components/Field";
import { Table } from "../../components/Table";
import { t } from "../../i18n/t";

/**
 * MT-17 design §5/§6.8: "Unreconciled bank activity" -- one amount input per bank account the source
 * lists (Xero, once `stage_scope` has run), with its own PATCH so it works even before the general
 * "Save scope" form carries this key (it never will -- see `ScopeScreen`'s own `onSave`, UI9).
 *
 * Presentational, like `FxOpeningBalances`: `ScopeScreen` owns the query and the one mutation
 * (`useUpdateScope`, shared with the general Save) and hands this component data plus a callback.
 *
 * `accounts === null` means the source has no scope-time bank-account list at all (every connector but
 * Xero, or a Xero migration scoped before this deploy, §8 Q5) -- the section falls back to a read-only
 * view of whatever was declared. `accounts === []` (S6) is a REAL, distinct answer: the source is known
 * to have no bank accounts, so the operator can only clear a create-time typo, never declare a real one.
 */

const AMOUNT_RE = /^-?[0-9]{1,13}(\.[0-9]{1,6})?$/;

/**
 * Plain `record[key]` for a key equal to the literal string `"__proto__"` does not read an own property
 * at all -- it returns the object's PROTOTYPE (`Object.prototype` for every plain object here), a real
 * object, not `undefined`. A bank-account id is an opaque source-controlled string (a Xero `AccountID`
 * today; nothing rules out a stranger one from another connector later), so every keyed read in this
 * component goes through this guard rather than bare bracket access.
 */
function safeGet(record: Record<string, string>, key: string): string | undefined {
  return Object.hasOwn(record, key) ? record[key] : undefined;
}

export function SourceUnreconciled({
  accounts,
  declared,
  unknownKeys,
  derived,
  origin,
  baseCurrency,
  extractedAsAt,
  conversionDate,
  state,
  notStarted,
  frozen,
  saving,
  serverFieldErrors,
  onSave,
}: {
  accounts: SourceBankAccount[] | null;
  declared: Record<string, string>;
  unknownKeys: string[] | null;
  /** xmg-autoderive A: what the source's own books say per bank account (calculated suggestion or a plain
   * reason it could not be calculated); `null` when nothing was calculated for this migration. */
  derived: SourceUnreconciledDerived[] | null;
  /** Who owns the stored figure: the untouched calculated suggestion, or the operator's own entry. */
  origin: "derived" | "operator" | null;
  baseCurrency: string;
  extractedAsAt: string | null;
  conversionDate: string | null;
  state: MigrationState | undefined;
  notStarted: boolean;
  frozen: boolean;
  saving: boolean;
  /** F2/final decision 15: a 422's `details.fields[].loc[2]` -- keyed by account id, set by `ScopeScreen`
   * and cleared again on the next save attempt. Client-side `fieldErrors` below always wins over this: a
   * server error describes a value the operator has since edited. */
  serverFieldErrors?: Record<string, string>;
  onSave: (map: Record<string, string>) => void;
}): JSX.Element {
  const [drafts, setDrafts] = useState<Record<string, string>>(declared);
  const [seededFor, setSeededFor] = useState<string>(JSON.stringify(declared));
  const [removedUnknown, setRemovedUnknown] = useState<Set<string>>(new Set());
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // F1: editable only while awaiting scope sign-off, not frozen and the list is known.
  const editable = state === "awaiting_scope_signoff" && !frozen && accounts !== null;

  // Seeded from the server once, and RE-seeded whenever the server's own map changes content -- which
  // includes right after a successful save, once the query that fetched `declared` is invalidated and
  // refetched (design §5, "seeded from the server once and re-seeded after a successful save").
  const declaredKey = JSON.stringify(declared);
  useEffect(() => {
    if (declaredKey !== seededFor) {
      setDrafts(declared);
      setRemovedUnknown(new Set());
      setSeededFor(declaredKey);
    }
    // `declared` itself is intentionally not a dependency: only its CONTENT (via `declaredKey`) should
    // trigger a re-seed, so a re-render with a new-but-equal object does not clobber an in-progress edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [declaredKey, seededFor]);

  const derivedById = new Map((derived ?? []).map((entry) => [entry.account_id, entry]));

  // The row hint: the calculated sentence (or the plain reason it could not be calculated), then, for a
  // foreign-currency account, the existing "enter the base-currency equivalent" line.
  function hintFor(row: SourceBankAccount): string | undefined {
    const parts: string[] = [];
    const entry = derivedById.get(row.account_id);
    if (entry !== undefined) {
      if (entry.amount !== null && entry.amount !== undefined) {
        parts.push(
          t("scope.unreconciled_calculated", {
            count: String(entry.items),
            amount: entry.amount,
            base: baseCurrency,
          }),
        );
        if (origin === "operator" && safeGet(drafts, row.account_id)?.trim() !== entry.amount) {
          parts.push(t("scope.unreconciled_changed_by_you"));
        }
      } else if (entry.reason) {
        parts.push(t("scope.unreconciled_not_calculated", { reason: entry.reason }));
      }
    }
    if (row.currency.toUpperCase() !== baseCurrency.toUpperCase()) {
      parts.push(t("scope.unreconciled_foreign_hint", { currency: row.currency, base: baseCurrency }));
    }
    return parts.length === 0 ? undefined : parts.join(" ");
  }

  const pendingUnknown = (unknownKeys ?? []).filter((key) => !removedUnknown.has(key));

  function set(accountId: string, value: string): void {
    setDrafts((prev) => ({ ...prev, [accountId]: value }));
    setFieldErrors((prev) => {
      if (!Object.hasOwn(prev, accountId)) return prev;
      const next = { ...prev };
      delete next[accountId];
      return next;
    });
  }

  function handleSave(): void {
    if (accounts === null) return;
    const errors: Record<string, string> = {};
    for (const account of accounts) {
      const value = (safeGet(drafts, account.account_id) ?? "").trim();
      if (value !== "" && !AMOUNT_RE.test(value)) {
        errors[account.account_id] = t("scope.unreconciled_invalid");
      }
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    // F3: `Object.fromEntries`, never `obj[key] = ...` -- a declared key such as `__proto__` must never
    // reach the prototype. Only KNOWN accounts' non-empty trimmed inputs are ever sent; an unknown key is
    // never in this body regardless of `removedUnknown` (I7/UI2) -- `Remove` is a review gate (below),
    // not itself part of what gets sent.
    const body = Object.fromEntries(
      accounts
        .map((account) => [account.account_id, (safeGet(drafts, account.account_id) ?? "").trim()] as const)
        .filter(([, value]) => value !== ""),
    );
    onSave(body);
  }

  if (accounts === null) {
    const declaredEntries = Object.entries(declared);
    return (
      <section aria-labelledby="scope-unreconciled">
        <h2 id="scope-unreconciled">{t("scope.unreconciled_title")}</h2>
        <p>{notStarted ? t("scope.unreconciled_not_started") : t("scope.unreconciled_list_unknown")}</p>
        {declaredEntries.length === 0 ? null : (
          <ul>
            {declaredEntries.map(([key, value]) => (
              <li key={key}>
                {key}: {value}
              </li>
            ))}
          </ul>
        )}
      </section>
    );
  }

  const hasKnownAccounts = accounts.length > 0;
  // UI11/critique F4, and critique-round finding (MEDIUM): with NO known accounts at all, Save's only
  // possible job is clearing whatever is declared -- `handleSave` below always sends `{}` here, because
  // it is built from `accounts`, which is empty, regardless of `drafts`/`removedUnknown`. Gating it on
  // `unknownKeys` (what the SERVER still holds), not `pendingUnknown` (what is left after LOCAL, unsaved
  // Removes), means clicking "Remove" can never disable the only button that can actually clear the
  // key -- the on-screen hint says "Remove these before you can save", and it must still be true after
  // the operator does exactly that. Save only goes away once the server confirms nothing is declared.
  const canSave =
    editable && (hasKnownAccounts ? pendingUnknown.length === 0 : (unknownKeys ?? []).length > 0);

  return (
    <section aria-labelledby="scope-unreconciled">
      <h2 id="scope-unreconciled">{t("scope.unreconciled_title")}</h2>
      <p>
        {t("scope.unreconciled_hint", {
          base: baseCurrency,
          as_at: extractedAsAt ?? "",
          conversion_date: conversionDate ?? "",
        })}
      </p>
      {accounts.length === 0 ? (
        <p>{t("scope.unreconciled_no_accounts")}</p>
      ) : (
        <Table<SourceBankAccount>
          caption={t("scope.unreconciled_title")}
          rowKey={(row) => row.account_id}
          rows={accounts}
          columns={[
            {
              key: "account",
              header: t("scope.unreconciled_col_account"),
              render: (row) => (
                <>
                  {row.code} — {row.name}
                  {row.status === "ACTIVE" ? null : (
                    <>
                      {" "}
                      <Badge tone="neutral">{t("scope.unreconciled_archived")}</Badge>
                    </>
                  )}
                </>
              ),
            },
            { key: "currency", header: t("scope.unreconciled_col_currency"), render: (row) => row.currency },
            {
              key: "declared",
              header: t("scope.unreconciled_col_declared"),
              render: (row) => (
                <Field
                  label={t("scope.unreconciled_amount_label_for", { account: `${row.code} — ${row.name}` })}
                  hint={hintFor(row)}
                  error={
                    safeGet(fieldErrors, row.account_id) ??
                    safeGet(serverFieldErrors ?? {}, row.account_id) ??
                    null
                  }
                >
                  {({ id: fieldId, describedBy }) => (
                    <input
                      id={fieldId}
                      inputMode="decimal"
                      aria-describedby={describedBy}
                      disabled={!editable || saving}
                      value={safeGet(drafts, row.account_id) ?? ""}
                      placeholder={t("scope.unreconciled_not_declared")}
                      onChange={(event) => set(row.account_id, event.target.value)}
                    />
                  )}
                </Field>
              ),
            },
          ]}
        />
      )}

      {pendingUnknown.length === 0 ? null : (
        <section aria-labelledby="scope-unreconciled-unknown">
          <h3 id="scope-unreconciled-unknown">{t("scope.unreconciled_unknown_title")}</h3>
          <p>{t("scope.unreconciled_unknown_hint")}</p>
          <ul>
            {pendingUnknown.map((key) => (
              <li key={key}>
                <Badge tone="warn">{t("scope.unreconciled_unknown_badge")}</Badge> {key}:{" "}
                {safeGet(declared, key) ?? ""}{" "}
                <Button
                  aria-label={t("scope.unreconciled_remove_for", { key })}
                  disabled={!editable}
                  onClick={() => setRemovedUnknown((prev) => new Set(prev).add(key))}
                >
                  {t("scope.unreconciled_remove")}
                </Button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {editable ? (
        <Button variant="primary" disabled={!canSave || saving} onClick={handleSave}>
          {t("scope.unreconciled_save")}
        </Button>
      ) : null}
    </section>
  );
}
